import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  message,
  Select,
  Space,
  Card,
  Row,
  Col,
  Tabs,
  Pagination
} from 'antd';
import {
  BellOutlined,
  CheckSquareOutlined,
  MailOutlined,
  CalendarOutlined,
  ContainerOutlined,
  FileTextOutlined,
  GlobalOutlined,
  CheckOutlined
} from '@ant-design/icons';
import './index.css';
import apiClient from '../../utils/apiService';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

function Notification() {
  const [messageApi, contextHolder] = message.useMessage();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState('all'); // all, 1, 0
  const [stats, setStats] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0); // 添加未读消息数状态
  const [form] = Form.useForm();

  // 获取未读消息数
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiClient.get('/message-notifications/my');
      const { unReadCount } = response.data.data || { unReadCount: 0 };
      setUnreadCount(unReadCount);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  // 获取统计数据
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/message-notifications/statistics');
      const statistics = response.data.data || [];
      
      // 直接保存原始统计数据
      setStats(statistics);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  // 定义不同类型消息的图标和颜色
  const getMessageConfig = (messageNode) => {
    const configs = {
      'NO MAN': { icon: <FileTextOutlined />, color: '#1f63fb' },
      'NO AN': { icon: <MailOutlined />, color: '#52c41a' },
      'LAST FREE DAY': { icon: <CalendarOutlined />, color: '#faad14' },
      'NO CARGO RELEASE': { icon: <ContainerOutlined />, color: '#f5222d' },
      'DEMURRAGE HAPPENING': { icon: <GlobalOutlined />, color: '#722ed1' },
      'DETENTION HAPPENING': { icon: <BellOutlined />, color: '#fa8c16' },
      'NO EMPTY RETURN': { icon: <CheckSquareOutlined />, color: '#13c2c2' },
      'ZZ': { icon: <GlobalOutlined />, color: '#8c8c8c' },
    };
    
    return configs[messageNode] || { icon: <BellOutlined />, color: '#1f63fb' };
  };


  // 渲染统计卡片
  const renderStatCards = () => {
      return stats.map((stat, index) => {
        const { icon, color } = getMessageConfig(stat.messageNode);
        return (
          <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
            <Card 
              style={{ 
                height: '120px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                marginBottom: 10,
              }}
              className='notify-stat-card'
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 24}}>
                <div style={{ fontSize: '20px', color }}>
                  {icon}
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: 4 }}>
                    {stat.messageNode}
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color }}>
                    {stat.count}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        );
      });
  };


  // 标记所有通知为已读
  const markAllAsRead = async () => {
    try {
      const response = await apiClient.post('/message-notifications/mark-all-read');
      messageApi.success(`成功标记 ${response.data.data || 0} 条消息为已读`);
      fetchNotifications({ page, size: pageSize });
      fetchStats();
      fetchUnreadCount(); // 重新获取未读消息数
    } catch (error) {
      messageApi.error('标记失败: ' + error.message);
    }
  };

  // 获取通知数据
  const fetchNotifications = useCallback(async (params = {}) => {
    const { page = 1, size = 10, ...otherFilters } = params;

    try {
      // 准备查询参数，合并搜索条件和额外参数
      const queryParams = {
        page, // 后端使用1基索引
        size,
        ...form.getFieldsValue(), // 包含搜索表单的条件
        ...otherFilters  // 包含其他可能的参数
      };

      // 根据选项卡状态添加读取状态过滤条件
      if (activeTab !== 'all') {
        if (activeTab === '0') {
          // 未读
          queryParams.readStatus = 0;
        } else if (activeTab === '1') {
          // 已读
          queryParams.readStatus = 1;
        }
      }

      const response = await apiClient.post('/message-notifications/page', queryParams);

      const { records, total } = response?.data?.data || { records: [], total: 0 };
      setNotifications(records || []);
      setTotal(total);
    } catch (error) {
      setNotifications([]);
      setTotal(0);
      messageApi.error(error.message || '数据加载失败');
    } finally {
      
    }
  }, [activeTab, messageApi]);


  // resolve 和 mark as read  
  const markAsResolved = async (itemId) => {
    try {
      await apiClient.post(`/message-notifications/${itemId}/solve`);
      messageApi.success('Marked as resolved');
      fetchNotifications({ page, size: pageSize });
      fetchStats();
      fetchUnreadCount();
    } catch (error) {
      messageApi.error('Failed to resolve: ' + error.message);
    }
  };

  const markAsRead = async (itemId) => {
    try {
      await apiClient.post(`/message-notifications/${itemId}/read`);
      messageApi.success('Marked as read');
      fetchNotifications({ page, size: pageSize });
      fetchStats();
      fetchUnreadCount();
    } catch (error) {
      messageApi.error('Failed to mark as read: ' + error.message);
    }
  };

  

  // 初始化加载数据
  useEffect(() => {
    fetchStats();
    fetchUnreadCount(); // 获取未读消息数
    fetchNotifications({ page: 1, size: pageSize });
  }, [activeTab, fetchStats, fetchUnreadCount, fetchNotifications]);

  // 表单提交
  const onFinish = (values) => {
    fetchNotifications({ 
      page: 1, 
      size: pageSize, 
      hblNo: values.hblNo,
      mblNo: values.mblNo,
      containerNo: values.containerNo,
      messageType: values.messageType
    });
    setPage(1);

    // 表单提交后默认激活全部选项卡
    setActiveTab('all')
  };

  // 重置表单
  const onReset = () => {
    form.resetFields();
    fetchNotifications({ page: 1, size: pageSize });
    setPage(1);

    // 重置后默认激活全部选项卡
    setActiveTab('all')
  };

  



  return (
    <div className="notification-container">
      {contextHolder}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, display: 'inline-block' }}>Notifications</h1>
        <div style={{ float: 'right', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 16 }}>
            You have <span style={{
              color: '#1f63fb',
              fontWeight: 'bold',
              margin: "0 4px"
            }}>{unreadCount}</span> unread notifications
          </span>
          <Button 
            type="primary" 
            icon={<CheckOutlined />}
            onClick={markAllAsRead}
          >
            Set All Read
          </Button>
        </div>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 12 }}>
        {renderStatCards()}
      </Row>

      {/* 搜索表单 */}
      <div className="search-form" 
        style={{ marginBottom: 10 }}>
        <Form
          form={form}
          name="search"
          onFinish={onFinish}
          onReset={onReset}
          layout="vertical"
        >
          <div className="form-items">
          <Form.Item name="hblNo" label="HBL" className="form-item">
            <Input placeholder="Input HBL" />
          </Form.Item>

          <Form.Item name="mblNo" label="MBL" className="form-item">
            <Input placeholder="Input MBL" />
          </Form.Item>

          <Form.Item name="containerNo" label="Container No." className="form-item">
            <Input placeholder="Input Container No." />
          </Form.Item>

          <Form.Item name="messageType" label="messageType" className="form-item">
            <Select placeholder="Select messageType">
              <Select.Option value="">All Types</Select.Option>
              <Select.Option value="1">NO MAN</Select.Option>
              <Select.Option value="2">NO AN</Select.Option>
              <Select.Option value="3">LAST FREE DAY</Select.Option>
              <Select.Option value="4">NO CARGO RELEASE</Select.Option>
              <Select.Option value="5">DEMURRAGE HAPPENING</Select.Option>
              <Select.Option value="6">DETENTION HAPPENING</Select.Option>
              <Select.Option value="7">NO EMPTY RETURN</Select.Option>
              <Select.Option value="8">ZZ</Select.Option>
              <Select.Option value="9">AI PARSE</Select.Option>
              
            </Select>
          </Form.Item>
          </div>

          <Form.Item
            style={{ marginTop: 20 }}
          >
            <Space>
              <Button type="primary" htmlType="submit">Search</Button>
              <Button htmlType="reset">Reset</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      {/* 选项卡 */}
      <Tabs 
        style={{ padding: '0 10px' }}
        activeKey={activeTab} 
        onChange={setActiveTab}
        type='card'
      >
        <TabPane tab="All" key="all" />
        <TabPane tab="Read" key={1} />
        <TabPane tab="Unread" key={0} />
      </Tabs>

      {/* 卡片列表 */}
      <div style={{ marginBottom: 16 }}>
        {
        (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
            {notifications.map((item) => (
              <div 
                key={item.id}
                style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  padding: '16px',
                  borderLeft: item.readStatus === 0 ? '6px solid #1f63fb' : '6px solid rgb(101, 199, 51)',
                  backgroundColor: '#fff',
                  opacity: item.solveStatus === 1 ? 0.6 : 1,
                  filter: item.solveStatus === 1 ? 'grayscale(0.6)' : 'none'
                }}
              >
                <div style={{ width: '10%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 4 }}>
                  <div style={{ fontSize: '18px', background: '#1f63fb', color: '#fff', width: '30px', height: '30px', borderRadius: '10%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <BellOutlined />
                  </div>
                </div>
                
                <div style={{ width: '60%', paddingLeft: 16 }}>
                  <div style={{ fontWeight: item.readStatus === 1 || item.solveStatus === 1 ? 'normal' : 'bold', fontSize: '16px', marginBottom: 8, color: '#333', textDecoration: item.solveStatus === 1 ? 'line-through' : 'none' }}>
                    {item.messageContent}
                  </div>
                  
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ fontWeight: 'bold', color: '#333' }}>MBL:</span> {item.mblNo || '-'}
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ fontWeight: 'bold', color: '#333' }}>HBL:</span> 
                      <a href={`/workorderdetail/${item.hblNo}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, color: '#1f63fb', textDecoration: 'none'}}>
                        {item.hblNo}
                      </a>
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ fontWeight: 'bold', color: '#333' }}>Container:</span> {item.containerNo || '-'}
                    </div>
                    <div>
                      <span style={{ fontWeight: 'bold', color: '#333' }}>Time:</span> {item.createTime ? dayjs(item.createTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </div>
                  </div>
                </div>
                
                <div style={{ width: '30%', height: 100, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div style={{ alignSelf: 'flex-end' }}>
                    {item.readStatus === 0 && (
                      <div style={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#1f63fb' 
                      }}></div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 8, width: '60%' }}>
                    {item.readStatus === 0 && <Button 
                      type="default" 
                      size="small"
                      style={{ width: '100%' }}
                      onClick={() => markAsRead(item.id)}
                    >
                      Mark as read
                    </Button>}

                    <Button 
                      type="default" 
                      size="small"
                      style={{ width: '100%' }}
                      onClick={() => markAsResolved(item.id)}
                      disabled={item.solveStatus === 1}
                    >
                      Resolve
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* 分页 */}
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <div style={{ display: 'inline-block' }}>
          <Pagination
            total={total}
            current={page}
            pageSize={pageSize}
            onChange={(newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
              fetchNotifications({ 
                page: newPage, 
                size: newPageSize 
              });
            }}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          />
        </div>
      </div>



    </div>
  );
}

export default Notification;